import {PermissionScope} from "./permission-scope.enum";
import {PermissionItem} from "./permission.item";
import {PermissionGrant} from "./permission-grant.enum";

export class Permissions {

  private permissions: Map<PermissionScope, PermissionItem> = new Map<PermissionScope, PermissionItem>();

  constructor(perms: Map<PermissionScope, PermissionGrant>) {
    perms.forEach((v, k) => {
      this.permissions.set(k, new PermissionItem(k, v));
    });
    Object.values(PermissionScope).filter((k) => !this.permissions.has(k)).forEach((k) => {
      this.permissions.set(k, new PermissionItem(k, PermissionGrant.NONE));
    });
  }

  public get contact(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.CONTACT);
  }

  public get contactEmployee(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.CONTACT_EMPLOYEE);
  }

  public get contactVehicle(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.CONTACT_VEHICLE);
  }

  public get contactVehicleExtended(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.CONTACT_VEHICLE_EXTENDED);
  }

  public get invoice(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.INVOICE);
  }

  public get invoiceCancel(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.INVOICE_CANCEL);
  }

  public get invoicePayment(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.INVOICE_PAYMENT);
  }

  public get receipt(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.RECEIPT);
  }

  public get receiptPayment(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.RECEIPT_PAYMENT);
  }

  public get article(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.ARTICLE);
  }

  public get oldPart(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.OLD_PART);
  }

  public get catalog(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.CATALOG);
  }

  public get vehicle(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.VEHICLE);
  }

  public get settings(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.SETTINGS);
  }

  public get settingsOrg(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.SETTINGS_ORG);
  }

  public get settingsUser(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.SETTINGS_USER);
  }

  public get settingsInvoice(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.SETTINGS_INVOICE);
  }

  public get settingsVatRate(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.SETTINGS_VAT_RATE);
  }

  public get stahlgruberCatalogImport(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.STAHLGRUBER_CATALOG_IMPORT);
  }

  public get feature(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.FEATURE);
  }

  public get billing(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.BILLING);
  }

  public get textTemplate(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.INVOICE);
  }

  public get statistics(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.STATISTICS);
  }

  public get orgWallet(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.ORG_WALLET);
  }

  public get correspondence(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.CORRESPONDENCE);
  }

  // A virtual permission that is made up of others
  public get export(): PermissionItem {
    const grants: PermissionGrant[] = [this.exportDatev.grant, this.exportCsv.grant, this.ironBackup.grant];
    return new PermissionItem(PermissionScope.EXPORT, this.getHighestPermissionGrant(grants));
  }

  public get exportDatev(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.EXPORT_DATEV);
  }

  public get exportCsv(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.EXPORT_CSV);
  }

  public get ironBackup(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.IRON_BACKUP);
  }

  public get support(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.SUPPORT);
  }

  /** Zeichnungsberechtigung: wer AVV, Nutzungsbedingungen und Datenschutzerklärung für die Organisation annehmen darf. */
  public get agreement(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.AGREEMENT);
  }

  public get funeral(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.FUNERAL);
  }

  public get document(): PermissionItem {
    return this.getByScopeInternal(PermissionScope.DOCUMENT);
  }

  public getByScope(scope: PermissionScope): PermissionItem {
    if (scope === PermissionScope.EXPORT) {
      return this.export;
    }
    return this.getByScopeInternal(scope);
  }

  private getByScopeInternal(scope: PermissionScope): PermissionItem {
    return this.permissions.has(scope) ? this.permissions.get(scope)! : new PermissionItem(scope, PermissionGrant.NONE);
  }

  private getHighestPermissionGrant(grants: PermissionGrant[]): PermissionGrant {
    const p = {[PermissionGrant.NONE]: 0, [PermissionGrant.R]: 1, [PermissionGrant.RW]: 2, [PermissionGrant.RWD]: 3};
    return grants.reduce((h, c) => p[c] > p[h] ? c : h);
  }
}
